import React from 'react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Add, LocalOffer } from '@material-ui/icons';
import { uniqBy } from 'lodash';
import { ConsoleActionButton } from '../../common/ConsoleActionButton';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { TemplateSnackbar, SnackBarOptions } from '../../../../../common/snackbars/TemplateSnackbar';
import { ADMIN_CONSOLE_TAG_QUERY } from './admin-console-tags.gql';
import { ACTIVITY_TAGS_QUERY } from './admin-console-activity-tags.gql';
import { GetAdminConsoleTags } from './__generated__/GetAdminConsoleTags';
import { GetActivitisActivityTags } from './__generated__/GetActivitisActivityTags';
import { AdminConsoleTag as ActivityTag } from './__generated__/AdminConsoleTag';
import { FormTagDialog } from './FormTagDialog';
import { ActivitiesList } from './ActivitiesList';
import { TagItem, ExtendTag } from './TagItem/TagItem';
import { useDeepEffect } from '../../../../../hooks/useDeepEffect';
import { SearchField } from '../../../../../common/inputs/SearchField';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';

const useStyles = makeStyles(() =>
  createStyles({
    contentContainer: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '2.25rem',
    },
    sectionTitle: {
      fontWeight: 600,
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    sectionDescription: {
      fontSize: '14px',
      color: '#808080',
      marginTop: '0.25rem',
    },
    emptyBody: {
      fontSize: '14px',
      color: '#808080',
      marginTop: '1.5rem',
    },
  })
);

type ActivityTagType = { [key: string]: { [key: string]: boolean } };

export function ActivityTagsSection() {
  const classes = useStyles();
  const [snackbar, setSnackBar] = React.useState<SnackBarOptions>();
  const { data: tagsData, refetch, loading: getTagsLoading } = useCampfireQuery<GetAdminConsoleTags, {}>(
    ADMIN_CONSOLE_TAG_QUERY
  );
  const [showTagDialog, setShowTagDialog] = React.useState(false);
  const [showActivitiesList, setShowActivitiesList] = React.useState(false);
  const [selectedTag, setSelectedTag] = React.useState<ActivityTag>();
  const [searchInput, setSearchInput] = React.useState<string>();
  const apiUrl = useApiUrl();
  const { isSm } = useCampfireTheme();
  const campfireMutation = useCampfireFetch({ defer: true });
  const { data: activitiesData, refetch: refetchActivitiesTag } = useCampfireQuery<GetActivitisActivityTags, {}>(
    ACTIVITY_TAGS_QUERY
  );
  const [activitiesTagMap, setActivitiesTagMap] = React.useState<ActivityTagType>({});

  useDeepEffect(() => {
    if (activitiesData) {
      setActivitiesTagMap(
        activitiesData?.vm.activities.reduce(
          (acc, activity) => ({
            ...acc,
            [activity.activityId]: activity.activityTags.reduce(
              (accTag, currentTag) => ({
                ...accTag,
                [currentTag.activityTagId]: activity.activityTagged.some(
                  (activityTaggedItem) => activityTaggedItem.activityTag.activityTagId === currentTag.activityTagId
                ),
              }),
              {}
            ),
          }),
          {}
        )
      );
    }
  }, [activitiesData]);

  const onEdit = (tag: ActivityTag) => {
    setSelectedTag(tag);
    setShowTagDialog(true);
  };

  const onDelete = (tag: ActivityTag) => {
    campfireMutation
      .run({
        url: `${apiUrl}/vm/activity/tag/remove`,
        method: 'post',
        data: {
          activityTagId: tag.activityTagId,
        },
      })
      .then(() => {
        if (refetch) {
          setTimeout(refetch, 0);
        }
        setSnackBar({
          variant: 'success',
          open: true,
          message: 'Tag successfully removed',
        });
      });
  };

  const handleSuccess = (message: string) => {
    if (refetch) {
      setTimeout(refetch, 0);
    }
    handleClostDialogTag();
    setSnackBar({
      variant: 'success',
      open: true,
      message,
    });
  };

  const handleClostDialogTag = () => {
    setShowTagDialog(false);
    setSelectedTag(undefined);
  };

  const handleCloseActivitiesList = () => {
    setShowActivitiesList(false);
    setSelectedTag(undefined);
  };

  const handleChangeActivitiesList = (activityId: string) => {
    if (!selectedTag?.activityTagId) {
      return;
    }

    const activityTagId = selectedTag?.activityTagId;

    const updatingActivityTags = {
      ...activitiesTagMap,
      [activityId]: {
        ...activitiesTagMap[activityId],
        [activityTagId]: !activitiesTagMap[activityId][activityTagId],
      },
    };
    setActivitiesTagMap(updatingActivityTags);

    campfireMutation
      .run({
        url: `${apiUrl}/vm/activity/update-activity-tag`,
        method: 'post',
        data: {
          activityId,
          activityTagIds: Object.keys(updatingActivityTags[activityId]).filter(
            (key) => updatingActivityTags[activityId][key]
          ),
        },
      })
      .then(() => {
        setSnackBar({
          variant: 'success',
          open: true,
          message: 'Activity tag saved!',
        });
        if (refetchActivitiesTag) {
          setTimeout(refetchActivitiesTag, 0);
        }
      });
  };
  const handleShowActivitiesList = (tag: ActivityTag) => {
    setSelectedTag(tag);
    setShowActivitiesList(true);
  };

  const activityTags =
    tagsData?.vm.activityTags.map((activityTag) => ({
      ...activityTag,
      activityCount: (activitiesData?.vm.activities || []).filter((activity) =>
        activity.activityTags.some(
          (activitisActivityTag) =>
            activitisActivityTag.activityTagId === activityTag.activityTagId &&
            activity.activityTagged.some(
              (activityTaggedItem) =>
                activityTaggedItem.activityTag.activityTagId === activitisActivityTag.activityTagId
            )
        )
      ).length,
    })) || [];

  const selectableActivities = activitiesData?.vm.activities.map((activity) => ({
    ...activity,
    checked: Boolean(
      selectedTag?.activityTagId &&
        activitiesTagMap[activity.activityId] &&
        activitiesTagMap[activity.activityId][selectedTag?.activityTagId]
    ),
  }));

  const programOptions = activitiesData?.vm.activities
    ? uniqBy(
        activitiesData?.vm.activities.map((activity) => ({
          value: activity.program.programId,
          label: activity.program.name,
        })),
        'value'
      )
    : [];

  const filteredActivityTags = React.useMemo(
    () => activityTags.filter((at) => (searchInput ? at.name.toLowerCase().includes(searchInput.toLowerCase()) : true)),
    [activityTags, searchInput]
  );

  return (
    <Box className={classes.contentContainer}>
      <Box
        display='flex'
        flexDirection={isSm ? 'column' : 'row'}
        alignItems={isSm ? 'left' : 'flex-start'}
        justifyContent='space-between'
      >
        <Box>
          <Typography className={classes.sectionTitle}>
            <LocalOffer style={{ fontSize: '14px', paddingRight: '5px' }} /> Activity Tags
          </Typography>
          <Typography variant={'body2'} className={classes.sectionDescription}>
            {
              'Activity tags are a great way to categorise your activities outside the existing program and activity structure, such as types of activities, corporate involvement, tracking assets, statuses, and more.'
            }
          </Typography>
        </Box>
        <Box
          display='flex'
          alignItems='center'
          style={{ gap: 12, marginLeft: isSm ? 0 : '0.75rem', paddingTop: isSm ? '1rem' : 0, flexShrink: 0 }}
        >
          <SearchField placeholder='Search Tags' onChange={(e) => setSearchInput(e.target.value)} fullWidth={isSm} />
          <ConsoleActionButton
            endIcon={<Add />}
            variant='contained'
            color='primary'
            onClick={() => setShowTagDialog(true)}
          >
            Add Tag
          </ConsoleActionButton>
        </Box>
      </Box>

      {activityTags?.length ? (
        <Box marginTop='1.25rem'>
          {filteredActivityTags.map((tag: ExtendTag) => (
            <TagItem
              key={tag.activityTagId}
              tag={tag}
              onEdit={onEdit}
              onDelete={onDelete}
              onShowActivities={handleShowActivitiesList}
              isLoading={getTagsLoading}
            />
          ))}
        </Box>
      ) : (
        <Typography variant={'body2'} className={classes.emptyBody}>
          You haven’t created any activity tags yet. Click the ‘<strong>Add Tag</strong>’ button to add your first.
        </Typography>
      )}
      {showActivitiesList && (
        <ActivitiesList
          activities={selectableActivities || []}
          onClose={handleCloseActivitiesList}
          title={selectedTag?.name || ''}
          onChange={handleChangeActivitiesList}
          programOptions={programOptions}
        />
      )}
      {showTagDialog && <FormTagDialog tag={selectedTag} onClose={handleClostDialogTag} onSuccess={handleSuccess} />}
      {snackbar && <TemplateSnackbar {...snackbar} onClose={() => setSnackBar(undefined)} />}
    </Box>
  );
}
